import { useTranslation } from "react-i18next";
import { Space, type TableColumnsType } from "antd";

import { UserAvatar } from "@av/components/molecules/EmployeeAvatar";
import { getConsistentColorForString } from "@av/components/utilities/color-utilities";
import { getInitials } from "@av/utils/domain/user/initials-utilities";
import {
  type AdminSettingsUserBase,
  headerCell,
  type HrAdminSettingsUser,
} from "../../../../../services/admin-settings/type";
import { compareText } from "../../../../hooks/filter-admin-settings-users";
import { CommonUserTable } from "../../common/user-table/CommonUserTable";
import { createEmailColumn } from "../../common/user-table/EmailColumn";
import { createSecurityGroupColumn } from "../../common/user-table/SecurityGroupColumn";

type UserTableProps = {
  users: HrAdminSettingsUser[];
  isLoading?: boolean;
  isDemo?: boolean;
  testIdPrefix: string;
  onEditUser?: (user: HrAdminSettingsUser) => void;
  onSecurityGroupClick?: (securityGroup: string, user: AdminSettingsUserBase) => void;
};

type SortableUserField = keyof Pick<
  HrAdminSettingsUser,
  "department" | "email" | "employeeNumber" | "jobTitle" | "name"
>;

function compareField(field: SortableUserField) {
  return (a: HrAdminSettingsUser, b: HrAdminSettingsUser) => compareText(a[field], b[field]);
}

export function UserTable(props: UserTableProps) {
  const {
    users = [],
    isLoading = false,
    isDemo = false,
    testIdPrefix,
    onEditUser,
    onSecurityGroupClick,
  } = props;
  const { t } = useTranslation();

  const columns: TableColumnsType<HrAdminSettingsUser> = [
    {
      title: t("platform.adminSettings.users_columns_employeeNumber"),
      dataIndex: "employeeNumber",
      key: "employeeNumber",
      defaultSortOrder: "ascend",
      sorter: compareField("employeeNumber"),
      onHeaderCell: headerCell(`${testIdPrefix}-column-employee-number`),
      width: 150,
    },
    {
      title: t("platform.adminSettings.users_columns_name"),
      dataIndex: "name",
      key: "name",
      sorter: compareField("name"),
      onHeaderCell: headerCell(`${testIdPrefix}-column-name`),
      width: 280,
      render: (name: string, user) => (
        <Space>
          <UserAvatar
            testIdPrefix={`${testIdPrefix}-avatar-${user.employeeNumber}`}
            info={{
              avatarUri: user.avatarUri,
              avatarColor: getConsistentColorForString(user.name),
              initials: getInitials(user.name),
            }}
            isDemo={isDemo}
          />
          {name}
        </Space>
      ),
    },
    createSecurityGroupColumn<HrAdminSettingsUser>({
      t,
      productCode: "hr",
      testIdPrefix,
      getTestIdValue: (user) => user.employeeNumber,
      onSecurityGroupClick,
    }),
    createEmailColumn<HrAdminSettingsUser>({
      t,
      testIdPrefix,
      getTestIdValue: (user) => user.employeeNumber,
    }),
    {
      title: t("platform.adminSettings.users_columns_department"),
      dataIndex: "department",
      key: "department",
      onHeaderCell: headerCell(`${testIdPrefix}-column-department`),
      width: 160,
    },
    {
      title: t("platform.adminSettings.users_columns_jobTitle"),
      dataIndex: "jobTitle",
      key: "jobTitle",
      onHeaderCell: headerCell(`${testIdPrefix}-column-job-title`),
      width: 240,
    },
  ];

  const handleEditUser = onEditUser;

  return (
    <CommonUserTable<HrAdminSettingsUser>
      users={users}
      isLoading={isLoading}
      testIdPrefix={testIdPrefix}
      onEditUser={handleEditUser}
      getTestIdValue={(user) => user.employeeNumber}
      t={t}
      dataColumns={columns}
    />
  );
}

export type { HrAdminSettingsUser, UserTableProps as AdminSettingsUserTableProps };
